import React, { useState } from "react";
import { Box, Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";

const rolePermissions = [
  {
    role: "Super Admin",
    tone: "navy",
    summary: "Fullest access across the application.",
    ui: [
      "All main menus, TH/PH client lists, notes, documents, admissions, export, roles, staff contacts, email templates, mobile settings and daily reads.",
      "Add Client button is visible.",
      "Admin tab is visible inside client profiles.",
    ],
    backend: [
      "Can manage roles and external access whitelist.",
      "Can write client data, notes, treatment, admissions, medical, finance, room operations, AI prompts and mobile content.",
      "Can delete daily reads and pass the Cloud Function user deletion check.",
    ],
  },
  {
    role: "Admin",
    tone: "blue",
    summary: "Broad operational access without role administration.",
    ui: [
      "Dashboard, client lists, notes, room layout, support dashboard, room bookings, room availability, documents, tutorials, registration, admissions, export and staff contacts.",
      "Admin tab is visible inside client profiles.",
      "Add Client button is not visible in the current UI.",
    ],
    backend: [
      "Can write most client, notes, treatment, admission, medical, finance, room, AI and mobile content data.",
      "Cannot manage roles or the external access whitelist.",
    ],
  },
  {
    role: "Clinical Director",
    tone: "indigo",
    summary: "Clinical leadership access.",
    ui: [
      "Dashboard, client lists, notes, room layout, support dashboard, room bookings, room availability, documents, tutorials, registration, admissions, roles and staff contacts.",
      "Admin tab is visible inside client profiles.",
      "Export, daily reads and mobile settings are not visible from the menu.",
    ],
    backend: [
      "Can write client, pre-admission, room, notes, treatment, clinical, admission, medical, psycho-ed and operational register data.",
      "Cannot write finance forms.",
      "Role writes remain Super Admin only in Firestore rules.",
    ],
  },
  {
    role: "Therapist",
    tone: "green",
    summary: "Clinical user focused on assigned clients and notes.",
    ui: [
      "Dashboard, My clients, TH/PH client lists, notes, room layout, support dashboard, room bookings, documents, tutorials, registration and staff contacts.",
      "My clients filters by assigned therapist.",
      "No Admin tab in client profiles.",
    ],
    backend: [
      "Can write client, pre-admission, notes, treatment, clinical forms, AI prompts, psycho-ed content and operational registers.",
      "Cannot write room operations, admission forms, medical forms, finance forms or mobile content.",
    ],
    notes:
      "Therapy note containers restrict therapists to their own notes in some views; Super Admin and Clinical Director see broader sets.",
  },
  {
    role: "Admission staff",
    tone: "orange",
    summary: "Admission-oriented access.",
    ui: [
      "Dashboard, TH/PH client lists, support dashboard, room bookings, room availability, documents, tutorials, registration, admissions and staff contacts.",
      "Add Client button is visible.",
      "Inside client profiles, Admission forms, Clinical documents, Notes and Discharge & Ongoing care are hidden.",
    ],
    backend: [
      "Can write client, pre-admission, room operations, notes, clinical forms, admission forms, AI prompts and operational registers.",
      "Cannot write treatment, medical, finance, psycho-ed or mobile content.",
    ],
  },
  {
    role: "Support Staff",
    tone: "teal",
    summary: "Operational support access.",
    ui: [
      "Dashboard, TH/PH client lists, notes, room layout, support dashboard, room bookings, documents, tutorials, registration and staff contacts.",
      "No Admin tab in client profiles.",
    ],
    backend: [
      "Can write client, pre-admission, room operations, notes, admission forms, AI prompts and operational registers.",
      "Cannot write treatment, clinical forms, medical forms, finance forms, psycho-ed content or mobile content.",
    ],
  },
  {
    role: "Medical Staff",
    tone: "red",
    summary: "Medical care access.",
    ui: [
      "Dashboard, TH/PH client lists, notes, room layout, support dashboard, room bookings, documents, tutorials, registration and staff contacts.",
      "Uses the medical-specific notes interface.",
      "No Admin tab in client profiles.",
    ],
    backend: [
      "Can write client, pre-admission, notes, treatment, clinical forms, medical forms and AI prompts.",
      "Cannot write room operations, admission forms, finance forms, psycho-ed content, operational registers or mobile content.",
    ],
  },
  {
    role: "Hospitality Staff",
    tone: "purple",
    summary: "Hospitality and room operations access.",
    ui: [
      "Dashboard, TH/PH client lists, notes, room layout, support dashboard, room bookings, documents, tutorials, registration and staff contacts.",
      "Admission forms and Clinical documents are hidden inside client profiles.",
      "Uses the hospitality-specific notes interface.",
    ],
    backend: [
      "Can write client data, room operations and operational registers.",
      "Firestore canWriteNotes does not include Hospitality Staff, so some note writes may fail even if the UI shows note actions.",
    ],
  },
  {
    role: "Psycho-ed",
    tone: "cyan",
    summary: "Psycho-education access.",
    ui: [
      "Dashboard, TH/PH client lists, notes, room layout, support dashboard, room bookings, documents, tutorials, registration and staff contacts.",
      "No Admin tab in client profiles.",
    ],
    backend: [
      "Can write client data, notes, AI prompts, psycho-ed content and operational registers.",
      "Cannot write pre-admission, room operations, treatment, clinical forms, admission forms, medical forms, finance forms or mobile content.",
    ],
  },
  {
    role: "Finance team",
    tone: "slate",
    summary: "Finance-oriented access.",
    ui: [
      "Dashboard, TH/PH client lists, notes, room layout, support dashboard, room bookings, documents, tutorials, registration and staff contacts.",
      "No Admin tab in client profiles.",
    ],
    backend: [
      "Can write client data and finance forms.",
      "Firestore canWriteNotes does not include Finance team, so note writes may fail even if the Notes menu is visible.",
      "Cannot write pre-admission, room operations, treatment, clinical, admission, medical, AI, psycho-ed, operational register or mobile content.",
    ],
  },
  {
    role: "External",
    tone: "gray",
    summary: "Limited external access.",
    ui: [
      "TH/PH client lists and My Profile only.",
      "Client list tabs are limited to All.",
    ],
    backend: [
      "Firestore read is allowed when the external custom claim is present.",
      "No write capability in the current role groups.",
      "Storage access is not generally included unless another condition applies.",
    ],
  },
  {
    role: "Normal User",
    tone: "gray",
    summary: "Default or not-yet-authorized account.",
    ui: ["My Profile only."],
    backend: [
      "No write permission groups.",
      "Login logic treats Normal User or missing role as not ready for normal app access.",
    ],
  },
];

function PermissionBlock({ icon, title, items }) {
  return (
    <div className="roles-permission-block">
      <div className="roles-permission-block-title">
        {icon}
        <span>{title}</span>
      </div>
      <ul>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

export default function RolesPermissionsOverview() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="roles-permissions-button"
        onClick={() => setOpen(true)}
      >
        <ShieldOutlinedIcon fontSize="small" />
        <span>View role permissions</span>
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="role-permissions-title"
        aria-describedby="role-permissions-description"
      >
        <Box className="roles-permissions-modal">
          <div className="roles-permissions-hero">
            <div>
              <div className="roles-permissions-kicker">Access matrix</div>
              <h2 id="role-permissions-title">Role permissions overview</h2>
              <p id="role-permissions-description">
                A practical summary of what each role can see in the interface
                and what the backend rules allow.
              </p>
            </div>
            <button
              type="button"
              className="roles-permissions-close"
              onClick={() => setOpen(false)}
              aria-label="Close permissions overview"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>

          <div className="roles-permissions-warning">
            UI access and backend access are not always identical. Backend
            rules are the final enforcement layer.
          </div>

          <div className="roles-permissions-grid">
            {rolePermissions.map((entry) => (
              <article
                key={entry.role}
                className={`roles-permission-card roles-permission-card-${entry.tone}`}
              >
                <div className="roles-permission-card-header">
                  <div>
                    <div className="roles-permission-role">{entry.role}</div>
                    <div className="roles-permission-summary">
                      {entry.summary}
                    </div>
                  </div>
                </div>

                <PermissionBlock
                  icon={<VisibilityOutlinedIcon fontSize="small" />}
                  title="UI access"
                  items={entry.ui}
                />
                <PermissionBlock
                  icon={<EditOutlinedIcon fontSize="small" />}
                  title="Backend permissions"
                  items={entry.backend}
                />
                {entry.notes && (
                  <div className="roles-permission-note">
                    <NotesOutlinedIcon fontSize="small" />
                    <span>{entry.notes}</span>
                  </div>
                )}
              </article>
            ))}
          </div>
        </Box>
      </Modal>
    </>
  );
}
